<script>
export default {
  name: 'ItineraryEditPosterUploadImages',
  props: {
    images: {
      type: Array,
      required: true,
      validator: value => value.every(img => typeof img === 'object' && 'selected' in img),
    },
    maxImages: {
      type: Number,
      default: 4,
      validator: value => value > 0 && value <= 10,
    },
    acceptedFileTypes: {
      type: Array,
      default: () => [ 'image/jpeg', 'image/png', 'image/webp' ],
    },
    uploadSuccess: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedImages: this.images.filter(image => image.selected).slice(0, this.maxImages),
      formImages: [],
      isUploading: false,
      uploadError: null,
      dragActive: false,
    };
  },
  computed: {
    remainingSlots() {
      const totalImages = this.selectedImages.length + this.formImages.length;
      return this.maxImages - totalImages;
    },
    canUpload() {
      return this.formImages.length > 0 && !this.isUploading;
    },
    uploadButtonText() {
      if (this.isUploading) return 'Uploading...';
      return `Upload ${this.formImages.length} ${this.formImages.length === 1 ? 'image' : 'images'}`;
    },
  },
  watch: {
    images: {
      handler(newVal) {
        this.selectedImages = newVal.filter(image => image.selected).slice(0, this.maxImages);
      },
      deep: true,
    },
    uploadSuccess(newVal) {
      if (newVal) {
        // Clear images only when parent confirms success
        this.formImages = [];
        this.isUploading = false;
      } else {
        // Reset uploading state if upload failed
        this.formImages = this.formImages.map(img => ({
          ...img,
          isUploading: false,
        }));
        this.isUploading = false;
      }
    },
  },
  created() {
    this.itineraryId = parseInt(this.$route.params.id, 10);
  },
  methods: {
    validateFile(file) {
      if (!this.acceptedFileTypes.includes(file.type)) {
        throw new Error(`File type ${file.type} is not supported`);
      }

      return true;
    },
    async handleImageUpload(event) {
      const files = event.target.files || event.dataTransfer.files;
      this.uploadError = null;

      try {
        const validFiles = Array.from(files).filter(file => {
          try {
            return this.validateFile(file);
          } catch (error) {
            this.uploadError = error.message;
            return false;
          }
        });

        const newPreviews = await Promise.all(
          validFiles.map(file => this.createPreview(file))
        );

        const availableSlots = this.remainingSlots;
        const filesToAdd = newPreviews.slice(0, availableSlots);
        this.formImages = [ ...this.formImages, ...filesToAdd ];

        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      } catch (error) {
        this.uploadError = error.message;
      }
    },
    createPreview(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => {
          resolve({
            file,
            name: file.name,
            preview: e.target.result,
            size: this.formatFileSize(file.size),
            isUploading: false,
          });
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = [ 'Bytes', 'KB', 'MB', 'GB' ];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    },
    handleDrop(event) {
      this.dragActive = false;
      this.handleImageUpload(event);
    },
    handleDragEnter() {
      this.dragActive = true;
    },
    handleDragLeave() {
      this.dragActive = false;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    removeFile(index) {
      this.formImages.splice(index, 1);
    },
    async uploadFiles() {
      if (!this.canUpload) return;

      this.isUploading = true;
      this.uploadError = null;

      // Mark all images as uploading
      this.formImages = this.formImages.map(img => ({
        ...img,
        isUploading: true,
      }));

      // Emit the upload event
      this.$emit('upload-images', this.formImages.map(img => img.file));

      // Don't clear the images here - let the parent component handle the success/failure
      // through props or events
    },
  },
};
</script>

<template>
  <div
    :class="{ 'dropzone-container--active': dragActive }"
    class="dropzone-container"
    @dragover.prevent
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
    @click="triggerFileInput"
  >
    <div class="dropzone-content">
      <div v-if="formImages.length === 0" class="dropzone-empty-state">
        <v-icon size="48" color="primary" class="mb-4">
          cloud_upload
        </v-icon>
        <h3 class="text-h6 mb-2">Drag & Drop Images Here</h3>
        <p class="text-body-2 text-medium-emphasis mb-4">
          or click to browse your files
        </p>
        <v-btn
          color="primary"
          variant="outlined"
          class="upload-button"
          @click.stop="triggerFileInput"
        >
          <v-icon left>
            add_photo_alternate
          </v-icon>
          Select Images
        </v-btn>
      </div>

      <div v-else class="image-grid">
        <div
          v-for="(file, index) in formImages"
          :key="`preview-${index}`"
          class="image-column"
          @click.stop
        >
          <div class="image-column__container">
            <div class="image-column__container--overlay">
              <v-btn
                icon
                variant="text"
                color="error"
                class="image-column__button--danger"
                aria-label="Remove image"
                @click.stop="removeFile(index)"
              >
                <v-icon>delete</v-icon>
              </v-btn>
            </div>
            <div v-if="file.isUploading" class="image-column__container--loading">
              <v-progress-circular
                indeterminate
                color="primary"
                size="32"
              />
            </div>
            <img
              :src="file.preview"
              :alt="file.name"
              class="image-column__container--img"
              loading="lazy"
            >
            <div class="image-column__container--info">
              <span class="image-name">{{ file.name }}</span>
              <span class="image-size">{{ file.size }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="upload-message" @click.stop>
      <template v-if="formImages.length > 0 && !isUploading">
        <v-btn
          :loading="isUploading"
          :disabled="!canUpload"
          color="primary"
          class="upload-button"
          @click.stop="uploadFiles"
        >
          <v-icon left>
            cloud_upload
          </v-icon>
          {{ uploadButtonText }}
        </v-btn>
      </template>

      <div class="upload-info">
        <v-icon
          size="small"
          color="primary"
          class="mr-1"
        >
          info
        </v-icon>
        <span class="text-body-2">
          You can add {{ remainingSlots }} more
          {{ remainingSlots === 1 ? 'image' : 'images' }}
        </span>
      </div>

      <v-alert
        v-if="uploadError"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-2"
      >
        {{ uploadError }}
      </v-alert>
    </div>

    <input
      ref="fileInput"
      :multiple="maxImages > 1"
      :accept="acceptedFileTypes.join(',')"
      type="file"
      class="hidden-input"
      @change="handleImageUpload"
    >
  </div>
</template>

<style scoped lang="scss">
.upload-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
}

.dropzone-container {
  border: 2px dashed rgba(25, 118, 210, 0.2);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.5);
  min-height: 300px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 24px;
  cursor: pointer;
  position: relative;

  &--active {
    border-color: rgb(25, 118, 210);
    background: rgba(25, 118, 210, 0.05);
    transform: scale(1.01);
  }

  &:hover {
    border-color: rgb(25, 118, 210);
    background: rgba(25, 118, 210, 0.05);
  }
}

.dropzone-content {
  display: flex;
  justify-content: center;
  min-height: 300px;
}

.dropzone-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  width: 100%;
}

.image-column {
  position: relative;
  aspect-ratio: 4/3;

  &:hover &__container--overlay {
    opacity: 1;
  }

  &__container {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    aspect-ratio: 4/3;
    width: 100%;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    }

    &--overlay {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      width: 100%;
      height: 100%;
      background-color: rgba(0,0,0,0.4);
      position: absolute;
      right: 0;
      top: 0;
      z-index: 10;
      opacity: 0;
      cursor: pointer;
      transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &--loading {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 20;
      backdrop-filter: blur(4px);
    }

    &--img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &--info {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 12px;
      background: linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0));
      color: white;
      font-size: 13px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
  }
}

.image-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

.image-size {
  opacity: 0.8;
  font-size: 12px;
}

.upload-button {
  min-width: 200px;
  font-weight: 500;
}

.upload-info {
  display: flex;
  align-items: center;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.hidden-input {
  display: none;
}
</style>



// WEBPACK FOOTER //
// src/components/itinerary/ItineraryEditPosterUploadImages.vue